// RITONA'S OWN UPKEEP (Scheduler Screen v2, the "Signal" re-layout): the registered Background Jobs —
// Collectors, housekeeping, reflection — behind a collapsible shelf so nothing runs where the human can't
// see it (G-112), without crowding the human Schedules. The header states the honest whole-shelf health;
// the expanded rows carry each job's module-recorded health (data-health), its next SERVER-projected
// occurrence, and its last Run Record marker with failed/interrupted visibly distinct (G-107/G-111).
// These jobs are config-owned: there is nothing to schedule or pause here, and the copy says so.
// Tokens only (G-181); no internal identifier (G-197/G-058).

import { useState } from 'react';
import type { BackgroundJobHealthRow } from '../projections.ts';
import { Icon } from '../../app/Icon.tsx';
import { needsAttention, runMarker } from './scheduler-format.ts';

/** One expanded upkeep row. */
function UpkeepRow({ row }: { readonly row: BackgroundJobHealthRow }): React.JSX.Element {
  const next = row.nextOccurrences[0];
  const last = row.recentRuns[0];
  const attention = needsAttention(row);
  return (
    <li className="up-row" data-health={row.health}>
      <span className={attention ? 'dot pending' : 'dot ok'} aria-hidden="true" />
      <span className="up-name">{row.name}</span>
      <span className="up-next">
        {next !== undefined ? `Next ${next.weekday} ${next.localDate}` : 'None projected'}
      </span>
      <span className="up-last">
        {last !== undefined ? (
          <span className={last.state === 'failed' || last.state === 'interrupted' ? 'att' : 'ok'}>
            Last {runMarker(last)}
          </span>
        ) : (
          'No runs recorded yet'
        )}
      </span>
      <span className="up-health mono-label">{row.health}</span>
    </li>
  );
}

/** The collapsible upkeep shelf for the registered Background Jobs. */
export function UpkeepShelf({
  jobs,
}: {
  readonly jobs: readonly BackgroundJobHealthRow[];
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (jobs.length === 0) return null;
  const attention = jobs.filter(needsAttention).length;
  return (
    <section aria-label="Ritona's own upkeep" className="up-card">
      <button
        type="button"
        className="up-head"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span className={open ? 'up-chev open' : 'up-chev'} aria-hidden="true">
          <Icon name="chevdown" size={14} />
        </span>
        <span className="up-title">Ritona&rsquo;s own upkeep</span>
        <span className="up-sub">
          {jobs.length} {jobs.length === 1 ? 'routine' : 'routines'} it runs for itself
        </span>
        <span className="spacer" aria-hidden="true" />
        <span className={attention === 0 ? 'up-state ok' : 'up-state att'}>
          <span className="dot" aria-hidden="true" />
          {attention === 0 ? 'All healthy' : `${attention} need a look`}
        </span>
      </button>
      {open ? (
        <div className="up-body">
          <div className="up-note">
            Housekeeping Ritona runs for itself — collecting values, reflecting overnight, keeping
            its own store tidy. There is nothing to schedule or pause here; it is listed so nothing
            runs where you can&rsquo;t see it.
          </div>
          <ul className="up-rows">
            {jobs.map((row) => (
              <UpkeepRow key={row.id} row={row} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
